import { WEEKDAYS } from '@/constants/weekdays';
import type { Translator } from '@/types/translator.type';
import type {
  ChildBandsCount,
  PastedPeriod,
  PastedPersonPrices,
} from '@/utils/benefits/hotel/excelPaste/types';
import type { ExcelPasteRecapRow } from './ExcelPastePanel';
import {
  formatPastedAmount,
  formatPastedDate,
} from './useExcelPasteFormatters';

export type ChildAges =
  | Array<{ childAgeFrom?: number; childAgeTo?: number }>
  | undefined;

export interface FormPeriod {
  id?: string;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  applicationDays?: string[];
}

/** The cards' `numberOfChildDistributions`, as the parsers expect it. */
export const toChildBandsCount = (
  numberOfChildDistributions: number,
): ChildBandsCount =>
  Math.min(Math.max(numberOfChildDistributions, 0), 2) as ChildBandsCount;

/** Price columns the page shows: adult, then older band, then younger band. */
export function getPersonPriceColumns(
  childBandsCount: ChildBandsCount,
  childAges: ChildAges,
  t: Translator,
): Array<{ field: keyof PastedPersonPrices; label: string }> {
  const columns: Array<{ field: keyof PastedPersonPrices; label: string }> = [
    { field: 'adultPrice', label: t('hotels.adult') },
  ];
  if (childBandsCount > 1 && childAges?.[1]) {
    columns.push({
      field: 'adolescentPrice',
      label: t('hotels.childAge', {
        ageFrom: childAges[1].childAgeFrom,
        ageTo: childAges[1].childAgeTo,
      }),
    });
  }
  if (childBandsCount > 0 && childAges?.[0]) {
    columns.push({
      field: 'childPrice',
      label: t('hotels.childAge', {
        ageFrom: childAges[0].childAgeFrom,
        ageTo: childAges[0].childAgeTo,
      }),
    });
  }
  return columns;
}

const formatPeriod = (period?: {
  startDate?: Date | string | null;
  endDate?: Date | string | null;
}) => {
  if (!period) return '';
  const start = formatPastedDate(period.startDate);
  const end = formatPastedDate(period.endDate);
  return start || end ? `${start} – ${end}` : '';
};

export function buildLabelAndPeriodsRecap(
  current: { name?: string | null; periods?: FormPeriod[] },
  pasted: { name: string; periods: PastedPeriod[] },
  t: Translator,
): ExcelPasteRecapRow[] {
  const currentPeriods = current.periods ?? [];
  const count = Math.max(currentPeriods.length, pasted.periods.length);
  return [
    {
      key: 'name',
      label: t('hotels.pasteFromExcel.fields.label'),
      current: current.name ?? '',
      pasted: pasted.name,
    },
    ...Array.from({ length: count }, (_, index) => ({
      key: `period-${index}`,
      label: t('hotels.pasteFromExcel.fields.period', { index: index + 1 }),
      current: formatPeriod(currentPeriods[index]),
      pasted: formatPeriod(pasted.periods[index]),
    })),
  ];
}

export const formatRecapAmount = formatPastedAmount;

/**
 * Periods written by a paste. The grids leave the valid days out of the
 * block, so every period takes the days of the tariff's first period, and
 * keeps the id of the period at the same position so saving updates rows in
 * place instead of recreating them.
 */
export function buildPastedFormPeriods(
  currentPeriods: FormPeriod[] | undefined,
  pastedPeriods: PastedPeriod[],
): FormPeriod[] {
  const firstDays = currentPeriods?.[0]?.applicationDays;
  const applicationDays =
    firstDays && firstDays.length > 0 ? firstDays : [...WEEKDAYS];
  return pastedPeriods.map(({ startDate, endDate }, index) => {
    const id = currentPeriods?.[index]?.id;
    return {
      ...(id ? { id } : {}),
      startDate,
      endDate,
      applicationDays: [...applicationDays],
    };
  });
}

/** Field names of the pasted periods, for the highlight. */
export const getPeriodFieldNames = (periodsPath: string, count: number) =>
  Array.from({ length: count }, (_, index) => [
    `${periodsPath}.${index}.startDate`,
    `${periodsPath}.${index}.endDate`,
  ]).flat();

/**
 * Notes of a paste of several tariffs: the tariffs it adds, those it updates
 * and those it leaves as they are, by label.
 */
export function buildTariffNotes(
  {
    added,
    updated,
    untouched,
  }: { added: string[]; updated: string[]; untouched: string[] },
  t: Translator,
): string[] {
  const list = (names: string[]) =>
    names
      .map((name) =>
        t('hotels.pasteFromExcel.quoted', {
          name,
          interpolation: { escapeValue: false },
        }),
      )
      .join(', ');
  return (
    [
      ['addedTariffs', added],
      ['updatedTariffs', updated],
      ['untouchedTariffs', untouched],
    ] as const
  ).flatMap(([key, names]) =>
    names.length > 0
      ? [
          t(`hotels.pasteFromExcel.notes.${key}`, {
            tariffs: list(names),
            interpolation: { escapeValue: false },
          }),
        ]
      : [],
  );
}
